import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { updateCertification, updateSection, updateQuestion } from '../../actions/certificationActions'
import { reorderQuestions } from '../../reducers/certificationBuilderReducer'
import { isPercentage, showGenericError } from '../../utility/certificationBuilder'
import { useBuilderSensors, dragEndIds } from './sortable'
import { retakeOptions, findOption } from './certificationOptions'
import EditorHeader from './EditorHeader'
import BuilderField from './BuilderField'
import QuestionCard from './QuestionCard'
import Input from '../common/form/Input'
import Select from '../common/form/Select'
import DeleteConfirmationModal from './DeleteConfirmationModal'

import classes from './QuizEditor.module.scss'

const QuizEditor = ({ certificationId, quiz, sectionNumber, onDeleted }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const sensors = useBuilderSensors()
  const [passMark, setPassMark] = useState(String(quiz.passMark))
  const [passMarkError, setPassMarkError] = useState(null)
  const [openQuestionId, setOpenQuestionId] = useState(null)
  const [newQuestion, setNewQuestion] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const finalQuiz = !quiz.sectionId
  const retakes = retakeOptions()

  const saveQuiz = (attributes) => dispatch(finalQuiz
    ? updateCertification({ id: certificationId, finalQuizAttributes: { id: quiz.id, ...attributes } })
    : updateSection({ certificationId, id: quiz.sectionId, quizAttributes: { id: quiz.id, ...attributes } })).unwrap()

  const saveSettings = (attributes) => saveQuiz(attributes)
    .catch((errors) => (errors?.['finalQuiz.passMark'] || errors?.['quiz.passMark']
      ? setPassMarkError(t('certificationBuilder.quiz.passMarkError'))
      : showGenericError()))

  const savePassMark = () => {
    const value = Number(passMark)
    if (value === quiz.passMark) { return }
    if (!isPercentage(passMark)) { setPassMarkError(t('certificationBuilder.quiz.passMarkError')); return }

    saveSettings({ passMark: value })
  }

  const handleDragEnd = (event) => {
    const ids = dragEndIds(event)
    if (!ids) { return }

    dispatch(reorderQuestions({ quizId: quiz.id, ...ids }))
    const position = quiz.questions.findIndex(question => question.id === ids.overId) + 1
    dispatch(updateQuestion({ quizId: quiz.id, id: ids.activeId, position })).unwrap().catch(showGenericError)
  }

  const handleDelete = () => {
    setIsDeleting(true)
    saveQuiz({ _destroy: true })
      .then(onDeleted)
      .catch(() => { showGenericError(); setIsDeleting(false) })
  }

  const addQuestion = () => { setNewQuestion(true); setOpenQuestionId(null) }

  return (
    <div>
      <EditorHeader
        eyebrow={finalQuiz
          ? t('certificationBuilder.quiz.finalEyebrow')
          : t('certificationBuilder.quiz.sectionEyebrow', { section: sectionNumber })}
        action={!finalQuiz && (
          <button className={classes.textButton} onClick={() => setShowDeleteModal(true)}>{t('certificationBuilder.quiz.remove')}</button>
        )}
      />

      <div className={classNames('d-flex flex-column', classes.body)}>
        <div className={classes.settings}>
          <BuilderField label={t('certificationBuilder.quiz.passMark')} htmlFor='quizPassMark'>
            <Input
              id='quizPassMark'
              type='number'
              min={1}
              max={100}
              value={passMark}
              endAdornment={<span className={classes.adornment}>%</span>}
              onChange={(e) => { setPassMark(e.target.value); setPassMarkError(null) }}
              onBlur={savePassMark}
              error={passMarkError}
            />
          </BuilderField>
          <BuilderField label={t('certificationBuilder.quiz.retakes')} htmlFor='quizRetakes'>
            <Select
              inputId='quizRetakes'
              options={retakes}
              value={findOption(retakes, quiz.maxAttempts)}
              onChange={(option) => saveSettings({ maxAttempts: option.value })}
              isSearchable={false}
            />
          </BuilderField>
        </div>

        <div className={classNames('d-flex align-items-baseline justify-content-between', classes.questionsHeader)}>
          <span className={classes.questionsTitle}>
            {t('certificationBuilder.quiz.questions')}
            <span className={classes.questionsCount}> · {t('certificationBuilder.quiz.questionsCount', { count: quiz.questions.length })}</span>
          </span>
          <button className={classes.linkButton} onClick={addQuestion} disabled={newQuestion}>
            <i className='fa fa-plus mr-1' aria-hidden='true' />
            {t('certificationBuilder.quiz.addQuestion')}
          </button>
        </div>

        {quiz.questions.length === 0 && !newQuestion && (
          <button className={classes.emptyQuestions} onClick={addQuestion}>{t('certificationBuilder.quiz.noQuestions')}</button>
        )}

        <div className='d-flex flex-column'>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={quiz.questions.map(question => question.id)} strategy={verticalListSortingStrategy}>
              {quiz.questions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  quizId={quiz.id}
                  question={question}
                  number={index + 1}
                  open={openQuestionId === question.id}
                  onToggle={() => setOpenQuestionId(openQuestionId === question.id ? null : question.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
          {newQuestion && (
            <QuestionCard quizId={quiz.id} number={quiz.questions.length + 1} open onToggle={() => {}} onClose={() => setNewQuestion(false)} />
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        show={showDeleteModal}
        title={t('certificationBuilder.quiz.deleteTitle')}
        message={t('certificationBuilder.quiz.deleteMessage', { count: quiz.questions.length })}
        proceedMessage={t('common.action.remove')}
        isLoading={isDeleting}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleDelete}
      />
    </div>
  )
}

QuizEditor.propTypes = {
  certificationId: PropTypes.number.isRequired,
  quiz: PropTypes.shape({
    id: PropTypes.number.isRequired,
    sectionId: PropTypes.number,
    passMark: PropTypes.number.isRequired,
    maxAttempts: PropTypes.number,
    questions: PropTypes.array.isRequired,
  }).isRequired,
  sectionNumber: PropTypes.number,
  onDeleted: PropTypes.func.isRequired,
}

export default QuizEditor
